import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Save, User, MapPin, Phone, Heart, CreditCard, Lock, Mail, Camera, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientProfile() {
  const { user, reload } = useAuth();
  const [form, setForm] = useState({
    full_name: '', phone: '',
    address_line1: '', address_line2: '', city: '', state: '', postal_code: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  });
  const [saving, setSaving] = useState(false);
  const avatarRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || '', phone: user.phone || '',
        address_line1: user.address_line1 || '', address_line2: user.address_line2 || '',
        city: user.city || '', state: user.state || '', postal_code: user.postal_code || '',
        emergency_contact_name: user.emergency_contact_name || '',
        emergency_contact_phone: user.emergency_contact_phone || '',
        emergency_contact_relationship: user.emergency_contact_relationship || '',
      });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch('/auth/me', form);
      await reload();
      toast.success('Profile saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  /**
   * Owner self-upload of their profile photo. Uploads the raw file to
   * /api/files/upload (purpose=avatar), then pins the returned URL on
   * the user via PATCH /auth/me \u2014 which already honours `avatar_url`
   * in UserProfileUpdate. The <Avatar> re-renders instantly after reload.
   */
  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purpose', 'avatar');
      const { data: uploaded } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.patch('/auth/me', { avatar_url: uploaded.url });
      await reload();
      toast.success('Profile photo updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingAvatar(false); if (avatarRef.current) avatarRef.current.value = ''; }
  };

  return (
    <div className='p-6 lg:p-8 max-w-4xl mx-auto'>
      <div className='mb-6 flex items-start gap-4'>
        {/* Avatar + camera-overlay button \u2014 lets the owner swap their
            profile photo without hunting through a sub-menu. */}
        <div className='relative shrink-0'>
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.full_name || 'You'}
              className='w-20 h-20 rounded-full object-cover shadow-lg border-2 border-white dark:border-slate-800'
              data-testid='profile-avatar-img'
            />
          ) : (
            <div
              className='w-20 h-20 rounded-full bg-[#1B3A87] text-white flex items-center justify-center font-display font-bold text-3xl shadow-lg'
              data-testid='profile-avatar-fallback'
            >
              {user?.full_name?.[0] || '?'}
            </div>
          )}
          <button
            type='button'
            onClick={() => avatarRef.current?.click()}
            className='absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white flex items-center justify-center shadow-md border-2 border-white dark:border-slate-800 disabled:opacity-60'
            disabled={uploadingAvatar}
            aria-label='Change profile photo'
            data-testid='profile-avatar-upload-btn'
          >
            {uploadingAvatar ? <RotateCcw className='w-4 h-4 animate-spin' /> : <Camera className='w-4 h-4' />}
          </button>
          <input
            ref={avatarRef}
            type='file'
            accept='image/*'
            className='hidden'
            onChange={onAvatarPick}
            data-testid='profile-avatar-upload-input'
          />
        </div>

        <div className='flex-1 min-w-0'>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Account</div>
          <h1 className='font-display text-3xl font-bold text-[#0B1220] dark:text-slate-50 mt-1'>My Profile</h1>
          <p className='text-slate-500 dark:text-slate-400 text-sm mt-1'>
            Keep your contact, address, and emergency info up to date &mdash; we use it for invoices, communications, and safety.
            Tap the <span className='font-semibold text-[#C8102E]'>camera</span> to swap your profile photo.
          </p>
        </div>
      </div>

      <form onSubmit={save} className='space-y-5'>
        <Card className='p-5 border border-slate-200'>
          <div className='flex items-center gap-2 mb-4 text-[#1B3A87]'>
            <User className='w-4 h-4' /> <h3 className='font-display font-semibold'>Personal</h3>
          </div>
          <div className='grid sm:grid-cols-2 gap-3'>
            <div><Label>Full Name</Label><Input value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})} data-testid='profile-name-input' /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} placeholder='(555) 123-4567' data-testid='profile-phone-input' /></div>
            <div className='sm:col-span-2'><Label>Email (read-only)</Label><Input value={user?.email || ''} disabled className='bg-slate-50' /></div>
          </div>
        </Card>

        <Card className='p-5 border border-slate-200'>
          <div className='flex items-center gap-2 mb-4 text-[#1B3A87]'>
            <MapPin className='w-4 h-4' /> <h3 className='font-display font-semibold'>Home Address</h3>
          </div>
          <div className='grid sm:grid-cols-2 gap-3'>
            <div className='sm:col-span-2'><Label>Address Line 1</Label><Input value={form.address_line1} onChange={(e)=>setForm({...form, address_line1: e.target.value})} placeholder='123 Main St' data-testid='profile-addr1-input' /></div>
            <div className='sm:col-span-2'><Label>Address Line 2 (optional)</Label><Input value={form.address_line2} onChange={(e)=>setForm({...form, address_line2: e.target.value})} placeholder='Apt, suite, etc.' data-testid='profile-addr2-input' /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} data-testid='profile-city-input' /></div>
            <div className='grid grid-cols-2 gap-3'>
              <div><Label>State</Label><Input value={form.state} onChange={(e)=>setForm({...form, state: e.target.value})} placeholder='TX' data-testid='profile-state-input' /></div>
              <div><Label>ZIP</Label><Input value={form.postal_code} onChange={(e)=>setForm({...form, postal_code: e.target.value})} placeholder='75001' data-testid='profile-zip-input' /></div>
            </div>
          </div>
        </Card>

        <Card className='p-5 border border-slate-200'>
          <div className='flex items-center gap-2 mb-4 text-[#C8102E]'>
            <Heart className='w-4 h-4' /> <h3 className='font-display font-semibold'>Emergency Contact</h3>
          </div>
          <div className='grid sm:grid-cols-3 gap-3'>
            <div><Label>Name</Label><Input value={form.emergency_contact_name} onChange={(e)=>setForm({...form, emergency_contact_name: e.target.value})} data-testid='profile-ec-name-input' /></div>
            <div><Label>Phone</Label><Input value={form.emergency_contact_phone} onChange={(e)=>setForm({...form, emergency_contact_phone: e.target.value})} data-testid='profile-ec-phone-input' /></div>
            <div><Label>Relationship</Label><Input value={form.emergency_contact_relationship} onChange={(e)=>setForm({...form, emergency_contact_relationship: e.target.value})} placeholder='Spouse, parent…' data-testid='profile-ec-rel-input' /></div>
          </div>
        </Card>

        <Card className='p-5 border border-slate-200 bg-slate-50'>
          <div className='flex items-center gap-2 mb-2 text-slate-600'>
            <CreditCard className='w-4 h-4' /> <h3 className='font-display font-semibold'>Saved Payment Methods</h3>
            <Badge className='bg-slate-200 text-slate-700 border-0 ml-1'>Coming soon</Badge>
          </div>
          <p className='text-sm text-slate-600 flex items-start gap-2'><Lock className='w-3.5 h-3.5 mt-0.5 text-slate-500' /> Add a card on file to streamline POS purchases. We never store your raw card number — Stripe stores it securely on their PCI-compliant servers; we only keep a reference. <span className='font-semibold'>This will activate as soon as the owner connects a Stripe account.</span></p>
        </Card>

        <div className='flex justify-end'>
          <Button type='submit' disabled={saving} className='bg-[#1B3A87] hover:bg-[#16306F] text-white h-11 px-6' data-testid='profile-save-button'>
            <Save className='w-4 h-4 mr-2' /> {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}
